import { ApiResponse, FetchStatusInterface } from "@lib/types";
import { createSignal } from "solid-js";

export function useFetch<T>() {
  const [data, setData] = createSignal<T | null>(null);
  const [isLoading, setIsloading] = createSignal(false);
  const [fetchStatus, setFetchStatus] = createSignal<FetchStatusInterface>({
    isError: false,
    isSuccess: false,
    statusCode: 0,
    message: "",
  });

  const fetchStatusDefault = {
    isError: false,
    isSuccess: false,
    statusCode: 0,
    message: "",
  } satisfies FetchStatusInterface;

  const resetFetch = () => {
    setIsloading(() => false);
    setData(() => null);
    setFetchStatus(() => ({ ...structuredClone(fetchStatusDefault) }));
  };

  const fetchData = async (requesterCallback: () => Promise<ApiResponse<T>>) => {
    setData(() => null);
    setIsloading(() => true);
    setFetchStatus((): FetchStatusInterface => {
      return { ...structuredClone(fetchStatusDefault) };
    });

    return requesterCallback()
      .then((resp) => {
        if (!resp.ok) {
          setFetchStatus((prevStatus): FetchStatusInterface => {
            return {
              ...structuredClone(prevStatus),
              statusCode: resp.status,
              message: resp.statusText,
            };
          });
          throw new Error(`Error. Status code: ${resp.status}. ${resp.statusText}`);
        }
        return resp.json();
      })
      .then((data: T) => {
        setData(() => data);
        setFetchStatus((prevStatus): FetchStatusInterface => {
          return {
            ...structuredClone(prevStatus),
            isSuccess: true,
            message: "Success!",
            statusCode: 200,
          };
        });
      })
      .catch((error: Error) => {
        setData(() => null);
        setFetchStatus((prevStatus): FetchStatusInterface => {
          return { ...structuredClone(prevStatus), isError: true, message: error.message };
        });
      })
      .finally(() => {
        setIsloading(() => false);
      });
  };

  return [data, isLoading, fetchStatus, fetchData, resetFetch] as const;
}
